// src/screens/sales/document/components/form/hooks/useForm.ts
import React, { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { v4 } from 'uuid'
import {
  DocumentInterface,
  DocumentLinkInterface,
  DocumentPayload,
  OptionItemInterface,
  ValidationResultInterface,
  ValidationRulesInterface,
} from '../../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../../salesApi/salesApi'
import { useValidation } from '../../../../../../common/hooks/useValidation'
import { useInput } from '../../../../../../common/hooks/useInput'
import { useFiles } from '../../../../../../common/hooks/useFiles'
import { useCommonLoader } from '../../../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../../../common/utils/functions/delayAction'
import { documentValidations } from '../../../../../../common/utils/constants'
import { EntityType } from '../../../../../../common/types/enums'
import { createTypeSelectList } from '../../../../../../common/utils/functions/createTypeSelectList'
import { entityTypeToText } from '../../../../../../common/utils/functions/enumToText'
import { selectCurrentCluster } from '../../../../../../store/slices/currentClusterSlice'

const validations: ValidationRulesInterface[] = documentValidations

export interface LinkRow {
  uid: string
  entityType: EntityType
  isAll: boolean
  entityIds: number[]
  sortOrder: string
}

export const ALLOWED_ENTITY_TYPES: EntityType[] = [
  EntityType.CLUSTER,
  EntityType.STORE,
  EntityType.CATEGORY,
  EntityType.ITEM,
  EntityType.EXECUTOR,
  EntityType.MOTIVATION,
  EntityType.PLACE,
  EntityType.PROMO,
  EntityType.ORDER,
  EntityType.COMPLEX_ORDER,
  EntityType.STOCK_DOCUMENT,
  EntityType.PRICE_DOCUMENT,
]

const buildLinkRows = (links?: DocumentLinkInterface[]): LinkRow[] => {
  if (!links || links.length === 0) return []

  const grouped = new Map<EntityType, DocumentLinkInterface[]>()
  for (const link of links) {
    const arr = grouped.get(link.entityType) || []
    arr.push(link)
    grouped.set(link.entityType, arr)
  }

  const rows: LinkRow[] = []
  grouped.forEach((group, entityType) => {
    const first = group[0]
    rows.push({
      uid: v4(),
      entityType,
      isAll: first.isAll,
      entityIds: first.isAll ? [] : group.map((l) => Number(l.entityId)).filter((n) => !isNaN(n)),
      sortOrder: String(first.sortOrder ?? 0),
    })
  })
  return rows
}

const toOptions = <T extends { id?: number | string; name?: string }>(
  arr: T[] | undefined,
  labelFn?: (x: T) => string,
): OptionItemInterface[] =>
  (arr || []).map((x) => ({
    id: String(x.id ?? ''),
    label: labelFn ? labelFn(x) : (x.name ?? ''),
    name: x.name ?? '',
    value: String(x.id ?? ''),
  }))

export const useForm = (
  data: DocumentInterface,
  setData: React.Dispatch<SetStateAction<DocumentInterface>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
  el?: DocumentInterface,
) => {
  const [salesApi] = useState(getSalesApi())
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[]>([])
  const [linkRows, setLinkRows] = useState<LinkRow[]>([])
  const [entityOptions, setEntityOptions] = useState<Record<number, OptionItemInterface[]>>({})
  const cache = useRef<Record<number, OptionItemInterface[]>>({})
  const originalFileRef = useRef<File | null>(null)
  const { files, setFiles, pairs, setPairs, dataInit, fetchFiles } = useFiles()
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()
  const { inputHandler } = useInput(setData)
  const currentCluster = useSelector(selectCurrentCluster)
  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )

  useEffect(() => {
    originalFileRef.current = null
    if (el) {
      setData(el)
      setLinkRows(buildLinkRows(el.links))
      if (el.filePath) fetchFiles([el.filePath])
    }
  }, [el, setData])

  useEffect(() => {
    if (el && files.length > 0 && !originalFileRef.current) {
      originalFileRef.current = files[0]
    }
  }, [files, el])

  const getValidationResult = (fieldName: string) =>
    validationResult.find((el) => el.name === fieldName)

  const fetchByType = useCallback(
    async (type: EntityType): Promise<OptionItemInterface[]> => {
      try {
        switch (type) {
          case EntityType.STORE: {
            const r = await salesApi.getStoreList()
            return toOptions(r?.data)
          }
          case EntityType.CATEGORY: {
            const r = await salesApi.getCategoryList()
            return toOptions(r?.data)
          }
          case EntityType.ITEM: {
            const r = await salesApi.getSaleItemList()
            return toOptions(r?.data, (x) => `#${x.id} ${x.name}`)
          }
          case EntityType.EXECUTOR: {
            const r = await salesApi.getExecutorList()
            return toOptions(r?.data)
          }
          case EntityType.MOTIVATION: {
            const r = await salesApi.getMotivationList()
            return toOptions(r)
          }
          case EntityType.PLACE: {
            const r = await salesApi.getPlaceList()
            return toOptions(r?.data)
          }
          case EntityType.PROMO: {
            const r = await salesApi.getPromoList()
            return toOptions(r?.data)
          }
          case EntityType.ORDER: {
            const r = await salesApi.getOrderList()
            return toOptions(r, (x) => `#${x.id}`)
          }
          case EntityType.COMPLEX_ORDER: {
            const r = await salesApi.getComplexOrderList()
            return toOptions(r, (x) => `#${x.id}`)
          }
          case EntityType.STOCK_DOCUMENT: {
            const r = await salesApi.getDocumentList()
            return toOptions(r, (x) => `#${x.id}`)
          }
          case EntityType.PRICE_DOCUMENT: {
            const r = await salesApi.getPriceDocumentList()
            return toOptions(r, (x) => `#${x.id}`)
          }
          default:
            return []
        }
      } catch {
        return []
      }
    },
    [salesApi],
  )

  const loadEntities = useCallback(
    async (type: EntityType) => {
      if (cache.current[type]) {
        setEntityOptions((prev) => ({ ...prev, [type]: cache.current[type] }))
        return
      }
      const list = await fetchByType(type)
      cache.current[type] = list
      setEntityOptions((prev) => ({ ...prev, [type]: list }))
    },
    [fetchByType],
  )

  useEffect(() => {
    const uniqueTypes = Array.from(new Set(linkRows.map((r) => r.entityType))).filter(
      (t) => t !== EntityType.CLUSTER,
    )
    uniqueTypes.forEach((t) => loadEntities(t))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkRows.length])

  const getTypeOptionsForRow = useCallback(
    (row: LinkRow): OptionItemInterface[] => {
      const used = new Set(linkRows.filter((r) => r.uid !== row.uid).map((r) => r.entityType))
      return createTypeSelectList(
        ALLOWED_ENTITY_TYPES.filter((t) => !used.has(t)) as unknown as Record<string, number>,
        entityTypeToText,
      )
    },
    [linkRows],
  )

  const canAddLinkRow = useMemo(
    () => linkRows.length < ALLOWED_ENTITY_TYPES.length,
    [linkRows.length],
  )

  const addLinkRow = useCallback(() => {
    const used = new Set(linkRows.map((r) => r.entityType))
    const type = ALLOWED_ENTITY_TYPES.find((t) => !used.has(t))
    if (!type) return
    const isCluster = type === EntityType.CLUSTER
    setLinkRows((prev) => [
      ...prev,
      {
        uid: v4(),
        entityType: type,
        isAll: false,
        entityIds: isCluster && currentCluster?.id ? [currentCluster.id] : [],
        sortOrder: '',
      },
    ])
    if (!isCluster) loadEntities(type)
  }, [linkRows, loadEntities, currentCluster])

  const removeLinkRow = useCallback((uid: string) => {
    setLinkRows((prev) => prev.filter((r) => r.uid !== uid))
  }, [])

  const updateLinkRow = useCallback((uid: string, patch: Partial<LinkRow>) => {
    setLinkRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, ...patch } : r)))
  }, [])

  const changeEntityType = useCallback(
    (uid: string, type: EntityType) => {
      setLinkRows((prev) =>
        prev.map((r) => {
          if (r.uid !== uid) return r
          if (type === EntityType.CLUSTER) {
            return {
              ...r,
              entityType: type,
              isAll: false,
              entityIds: currentCluster?.id ? [currentCluster.id] : [],
            }
          }
          return { ...r, entityType: type, entityIds: [], isAll: false }
        }),
      )
      if (type !== EntityType.CLUSTER) loadEntities(type)
    },
    [loadEntities, currentCluster],
  )

  const getOptionsForRow = useCallback(
    (row: LinkRow): OptionItemInterface[] => {
      const all = entityOptions[row.entityType] || []
      const usedInOtherRows = new Set<number>()
      linkRows.forEach((r) => {
        if (r.uid === row.uid) return
        if (r.entityType !== row.entityType) return
        r.entityIds.forEach((id) => usedInOtherRows.add(id))
      })
      return all.filter((opt) => !usedInOtherRows.has(Number(opt.id)))
    },
    [entityOptions, linkRows],
  )

  const multipleSearchSelectHandler = useCallback(
    (value: string | number | boolean, fieldName: string, type: string = '') => {
      if (!value) return
      const numValue = Number(value)
      if (isNaN(numValue)) return

      setLinkRows((prev) =>
        prev.map((r) => {
          if (r.uid !== fieldName) return r
          if (r.entityIds.includes(numValue)) return r
          return { ...r, entityIds: [...r.entityIds, numValue] }
        }),
      )
    },
    [],
  )

  const multipleSearchSelectRemoveHandler = useCallback(
    (value: string | number | boolean, fieldName: string) => {
      if (!value) return
      const numValue = Number(value)
      if (isNaN(numValue)) return

      setLinkRows((prev) =>
        prev.map((r) =>
          r.uid === fieldName
            ? { ...r, entityIds: r.entityIds.filter((id) => id !== numValue) }
            : r,
        ),
      )
    },
    [],
  )

  const sortOrderHandler = useCallback(
    (uid: string, value: string | number | boolean | (string | undefined)[]) => {
      const strValue = String(value).replace(/[^\d]/g, '')
      if (strValue.length > 1 && strValue[0] === '0') return
      setLinkRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, sortOrder: strValue } : r)))
    },
    [],
  )

  const isRowInvalid = useCallback((row: LinkRow) => !row.isAll && row.entityIds.length === 0, [])

  const buildLinksPayload = useCallback((): DocumentLinkInterface[] => {
    const result: DocumentLinkInterface[] = []
    for (const row of linkRows) {
      const sortOrder = Number(row.sortOrder) || 0
      if (row.isAll) {
        result.push({
          entityType: row.entityType,
          entityId: null,
          isAll: true,
          sortOrder,
        })
      } else {
        row.entityIds.forEach((id) =>
          result.push({
            entityType: row.entityType,
            entityId: id,
            isAll: false,
            sortOrder,
          }),
        )
      }
    }
    return result
  }, [linkRows])

  const formAction = useCallback(async () => {
    try {
      await showCommonLoader()
      await delayAction(500)

      const links = buildLinksPayload()
      const payload: DocumentPayload = {
        name: data.name,
        description: data.description,
        isPrivate: data.isPrivate ?? false,
        links: JSON.stringify(links),
      }

      const fileChanged = files.length > 0 && files[0] !== originalFileRef.current
      if (fileChanged) {
        const dt = dataInit(files)
        if (dt.length > 0) payload.file = dt
      }

      if (data.id) {
        await salesApi.updateDocumentFile(data.id, payload)
      } else {
        await salesApi.createDocumentFile(payload)
      }

      await getList()
      await hideCommonLoader()
      setModalActive(false)
    } catch (error) {
      await hideCommonLoader()
    }
  }, [
    data,
    files,
    dataInit,
    buildLinksPayload,
    salesApi,
    getList,
    setModalActive,
    showCommonLoader,
    hideCommonLoader,
  ])

  useEffect(() => {
    for (const item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) checkAndAddValidation(item)
    }
  }, [checkAndAddValidation, data])

  useEffect(() => {
    for (const item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) validate(item, data[item])
    }
  }, [data])

  const disabled = useMemo(() => {
    const hasErrors = validationResult.some((el) => el.notValid)
    const hasFile = files.filter((f) => !f.name.startsWith('poster_')).length >= 1
    const hasInvalidRows = linkRows.some((row) => !row.isAll && row.entityIds.length === 0)
    const noRows = linkRows.length === 0
    return hasErrors || !hasFile || hasInvalidRows || noRows
  }, [validationResult, files, linkRows])

  return {
    formAction,
    data,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    files,
    pairs,
    setFiles,
    setPairs,
    linkRows,
    addLinkRow,
    removeLinkRow,
    updateLinkRow,
    changeEntityType,
    getOptionsForRow,
    getTypeOptionsForRow,
    isRowInvalid,
    canAddLinkRow,
    multipleSearchSelectHandler,
    multipleSearchSelectRemoveHandler,
    sortOrderHandler,
  }
}
